// ============================================================
// Geräteverwaltung im Browser: Gerätearten, Zustände, Alter.
// Dieselben Listen wie backend/src/geraete/geraete.ts (dort getestet) —
// die Kennungen müssen übereinstimmen, sonst weist der Server ab.
// ============================================================

export type GeraetZustand = "gut" | "befriedigend" | "mangelhaft" | "gesperrt" | "unbekannt";

export const GERAETEARTEN: Array<{ id: string; de: string; en: string }> = [
  { id: "schaukel", de: "Schaukel", en: "Swing" },
  { id: "nestschaukel", de: "Nestschaukel", en: "Nest swing" },
  { id: "rutsche", de: "Rutsche", en: "Slide" },
  { id: "klettergeraet", de: "Klettergerät", en: "Climbing frame" },
  { id: "kletternetz", de: "Kletternetz", en: "Climbing net" },
  { id: "kombination", de: "Spielkombination", en: "Play structure" },
  { id: "wippe", de: "Wippe", en: "Seesaw" },
  { id: "federwippgeraet", de: "Federwippgerät", en: "Spring rider" },
  { id: "karussell", de: "Karussell / Drehgerät", en: "Roundabout" },
  { id: "seilbahn", de: "Seilbahn", en: "Zip line" },
  { id: "trampolin", de: "Trampolin", en: "Trampoline" },
  { id: "balancier", de: "Balancier- und Hangelelement", en: "Balance element" },
  { id: "sandspiel", de: "Sandspielbereich", en: "Sand play" },
  { id: "wasserspiel", de: "Wasserspiel", en: "Water play" },
  { id: "spielhaus", de: "Spielhaus", en: "Playhouse" },
  { id: "fitness", de: "Fitness- und Bewegungsgerät", en: "Fitness equipment" },
  { id: "ballspiel", de: "Ballspiel (Tor, Korb)", en: "Ball games" },
  { id: "tischtennis", de: "Tischtennisplatte", en: "Table tennis" },
  { id: "ausstattung", de: "Ausstattung (Bank, Tisch, Abfall)", en: "Furniture" },
  { id: "einfriedung", de: "Einfriedung (Zaun, Tor)", en: "Fencing" },
  { id: "sonstiges", de: "Sonstiges Gerät", en: "Other" },
];

const ART = new Map(GERAETEARTEN.map((a) => [a.id, a]));

export function artName(id: string, de = true): string {
  const a = ART.get(id);
  return a ? (de ? a.de : a.en) : id;
}

export const ZUSTAENDE: GeraetZustand[] = ["gut", "befriedigend", "mangelhaft", "gesperrt", "unbekannt"];

export const ZUSTAND: Record<GeraetZustand, { de: string; en: string; farbe: string; rang: number }> = {
  gut: { de: "gut", en: "good", farbe: "#5f8a3c", rang: 3 },
  befriedigend: { de: "befriedigend", en: "fair", farbe: "#bd8524", rang: 2 },
  mangelhaft: { de: "mangelhaft", en: "poor", farbe: "#c2591f", rang: 1 },
  gesperrt: { de: "gesperrt", en: "closed", farbe: "#a3201f", rang: 0 },
  unbekannt: { de: "unbekannt", en: "unknown", farbe: "#8a908f", rang: 4 },
};

export type Altersklasse = "0-5" | "6-10" | "11-15" | "16-20" | ">20" | "unbekannt";

export const ALTERSKLASSEN: Array<{ id: Altersklasse; de: string; en: string }> = [
  { id: "0-5", de: "bis 5 Jahre", en: "up to 5 years" },
  { id: "6-10", de: "6 bis 10 Jahre", en: "6–10 years" },
  { id: "11-15", de: "11 bis 15 Jahre", en: "11–15 years" },
  { id: "16-20", de: "16 bis 20 Jahre", en: "16–20 years" },
  { id: ">20", de: "über 20 Jahre", en: "over 20 years" },
  { id: "unbekannt", de: "Baujahr unbekannt", en: "year unknown" },
];

export function altersklasse(alter: number | null): Altersklasse {
  if (alter == null) return "unbekannt";
  if (alter <= 5) return "0-5";
  if (alter <= 10) return "6-10";
  if (alter <= 15) return "11-15";
  if (alter <= 20) return "16-20";
  return ">20";
}

/** Kurzname einer Art ohne Zusatz: „Ausstattung (Bank, Tisch, Abfall)" → „Ausstattung". */
export function artKurz(name: string): string {
  return (name.replace(/\s*\(.*\)\s*$/, "").split(" / ")[0] ?? name).trim();
}

/**
 * Anzeigename: die Bezeichnung und, wenn sie die Art nicht schon nennt, der
 * Kurzname der Art dahinter („Sitzbank · Ausstattung"). Vorher standen hier
 * doppelte Klammern: „Sitzbank (Ausstattung (Bank, Tisch, Abfall))".
 */
export function geraetLabel(g: { art: string; bezeichnung: string }, de = true): string {
  const name = artName(g.art, de);
  const kurz = artKurz(name);
  const b = g.bezeichnung.trim();
  if (!b) return kurz;
  if (b.toLowerCase() === kurz.toLowerCase() || b.toLowerCase() === name.toLowerCase()) return b;
  return `${b} · ${kurz}`;
}

/** Kartenaufruf einer Fläche mit geöffnetem Reiter (Geräte oder Mängel) */
export function karteZurFlaeche(projectId: string, layerId: string, featureId: string, form: "geraete" | "maengel" | "pruefung"): string {
  return `/karte?project=${encodeURIComponent(projectId)}&feature=${encodeURIComponent(`${layerId}:${featureId}`)}&form=${form}`;
}
